import { FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, Slider, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import React, { useEffect, useRef, useState } from 'react'
import { Color, ColorPicker } from 'material-ui-color';
import PokerCard from '../room/PokerCard'
import { CardImage, cardImageMap, CardImageType, CUSTOM_CARD_IMAGE_KEY, isCustomCardImageData } from '../../themes/theme';
import CardImageCropDialog from './CardImageCropDialog'

const useStyles = makeStyles((theme) => ({
    addCard: {
        width: 80,
        height: 120,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        cursor: 'pointer',
        border: `2px dashed ${theme.palette.primary.main}`,
        background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
        color: theme.palette.primary.main,
        transition: 'background-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
            background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        },
    },
    customWrap: {
        position: 'relative',
        display: 'inline-block',
    },
    editBtn: {
        position: 'absolute',
        top: -8,
        right: -8,
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.background.paper,
        color: theme.palette.primary.main,
        boxShadow: '0 1px 6px rgba(0,0,0,0.28)',
        zIndex: 2,
        padding: 0,
        '&:hover': {
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
    },
}))

interface ICustomCardBuilderProps {
    customCardColor: string,
    setCustomCardColor: (color: string) => void
    useCustomCardColor: boolean
    setUseCustomCardColor: (useColor: boolean) => void
    cardImage: string
    setCardImage: (cardImage: string) => void
    customCardImage: string
    setCustomCardImage: (customCarImage: string) => void
    cardOutlineColor: string
    setCardOutlineColor: (color: string) => void
    useCustomCardOutline: boolean
    setUseCustomCardOutline: (useOutline: boolean) => void
    cardOutlineThickness: number
    setCardOutlineThickness: (thickness: number) => void
}

export default function CustomCardBuilder({ customCardColor, setCustomCardColor, useCustomCardColor, setUseCustomCardColor,
    cardImage, setCardImage, customCardImage, setCustomCardImage,
    cardOutlineColor, setCardOutlineColor, useCustomCardOutline, setUseCustomCardOutline,
    cardOutlineThickness, setCardOutlineThickness }: ICustomCardBuilderProps) {
    const classes = useStyles()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const objectUrlRef = useRef<string>('')
    const [cropSrc, setCropSrc] = useState('')
    const [cropOpen, setCropOpen] = useState(false)

    const hasCustom = isCustomCardImageData(customCardImage)

    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current)
            }
        }
    }, [])

    function handleColorTypeChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setUseCustomCardColor(e.target.value === 'custom')
    }

    function handleCustomColorChanged(color: Color) {
        setCustomCardColor(`#${color.hex}`)
    }

    function handleOutlineTypeChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setUseCustomCardOutline(e.target.value === 'custom')
    }

    function handleOutlineColorChanged(color: Color) {
        if (!color || !color.hex) {
            return
        }
        setCardOutlineColor(`#${color.hex}`)
    }

    function handleOutlineThicknessChanged(_e: React.ChangeEvent<{}>, value: number | number[]) {
        if (typeof value === 'number') {
            setCardOutlineThickness(value)
        }
    }

    function handleImageCardClicked(key: string) {
        setCardImage(key)
    }

    function openFilePicker() {
        fileInputRef.current?.click()
    }

    function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files && e.target.files[0]
        e.target.value = ''
        if (!file || file.type.indexOf('image/') !== 0) {
            return
        }
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
        }
        const url = URL.createObjectURL(file)
        objectUrlRef.current = url
        setCropSrc(url)
        setCropOpen(true)
    }

    function closeCrop() {
        setCropOpen(false)
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
            objectUrlRef.current = ''
        }
        setCropSrc('')
    }

    function handleCropConfirm(dataUrl: string) {
        setCustomCardImage(dataUrl)
        setCardImage(CUSTOM_CARD_IMAGE_KEY)
        closeCrop()
    }

    function renderImagesList() {
        var useImages = Array.from(cardImageMap.entries())
        useImages.unshift(['none', new CardImageType(new CardImage("", 0, 0), "None")])
        const tiles = useImages.map(list =>
            <Grid key={'grid-' + list[0]} item>
                <PokerCard key={list[0]} selectKey={list[0]} value={"8"} hasEstimated={true} name={list[1].name}
                    image={list[1].image} onClick={handleImageCardClicked} isSelected={cardImage === list[0]} invertFlip={false}
                    inGame={false} isAnonymous={false} isHorizontal={false} emote={""} isZoomed={false} onEmoteFinished={function (): void { }} />
            </Grid>
        )

        if (hasCustom) {
            tiles.push(
                <Grid key="grid-custom" item>
                    <div className={classes.customWrap}>
                        <button
                            type="button"
                            className={classes.editBtn}
                            aria-label="Change custom card image"
                            onClick={(e) => {
                                e.stopPropagation()
                                openFilePicker()
                            }}
                        >
                            <EditIcon style={{ fontSize: 16 }} />
                        </button>
                        <PokerCard
                            selectKey={CUSTOM_CARD_IMAGE_KEY}
                            value={"8"}
                            hasEstimated={true}
                            name={'Custom'}
                            image={new CardImage(customCardImage, 80, 120)}
                            onClick={handleImageCardClicked}
                            isSelected={cardImage === CUSTOM_CARD_IMAGE_KEY}
                            invertFlip={false}
                            inGame={false}
                            isAnonymous={false}
                            isHorizontal={false}
                            emote={""}
                            isZoomed={false}
                            onEmoteFinished={function (): void { }}
                        />
                    </div>
                </Grid>
            )
        } else {
            tiles.push(
                <Grid key="grid-add-custom" item>
                    <div style={{ width: 128, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div
                            className={classes.addCard}
                            role="button"
                            tabIndex={0}
                            onClick={openFilePicker}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    openFilePicker()
                                }
                            }}
                        >
                            <AddIcon />
                            <Typography variant="caption" align="center" style={{ lineHeight: 1.2, padding: '0 6px' }}>
                                Add Custom
                            </Typography>
                        </div>
                        <Typography align="center" style={{ fontSize: 18, overflowWrap: "break-word", width: "120px", marginTop: 8 }}>
                            Custom
                        </Typography>
                    </div>
                </Grid>
            )
        }

        return tiles
    }

    return (
        <div style={{ width: '100%' }}>
            <div style={{
                display: 'flex',
                gap: 16,
                alignItems: 'flex-start',
                marginBottom: 16,
            }}>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <PokerCard key='frontCard' value={"8"} hasEstimated={true} name={'Back'}
                        invertFlip={false} image={null} isSelected={false} inGame={false} isAnonymous={false} isHorizontal={false} emote={""}
                        onEmoteFinished={function (): void { }} isZoomed={false} selectKey={'preview-back'} onClick={function (key: string): void { }} />
                    <PokerCard key='backCard' value={"8"} hasEstimated={true} name={'Front'} invertFlip={true}
                        image={null} isSelected={false} inGame={false} isAnonymous={false} isHorizontal={false} emote={""}
                        onEmoteFinished={function (): void { }} isZoomed={false} selectKey={'preview-front'} onClick={function (key: string): void { }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Card Color</FormLabel>
                        <RadioGroup aria-label="cardColor" name="cardColor" value={useCustomCardColor ? 'custom' : 'theme'} onChange={handleColorTypeChanged}>
                            <FormControlLabel value="theme" control={<Radio />} label="Derived from Theme" />
                            <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                        </RadioGroup>
                        {useCustomCardColor ?
                            <ColorPicker
                                value={customCardColor}
                                onChange={handleCustomColorChanged}
                                disableAlpha />
                            : <></>
                        }
                    </FormControl>
                    <FormControl component="fieldset" style={{ marginTop: 12 }}>
                        <FormLabel component="legend">Card Outline</FormLabel>
                        <RadioGroup aria-label="cardOutline" name="cardOutline" value={useCustomCardOutline ? 'custom' : 'default'} onChange={handleOutlineTypeChanged}>
                            <FormControlLabel value="default" control={<Radio />} label="White" />
                            <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                        </RadioGroup>
                        {useCustomCardOutline ?
                            <ColorPicker
                                value={cardOutlineColor}
                                onChange={handleOutlineColorChanged}
                                disableAlpha />
                            : <></>
                        }
                    </FormControl>
                    <FormControl component="fieldset" style={{ marginTop: 12, width: '100%', maxWidth: 280 }}>
                        <FormLabel component="legend">Thickness</FormLabel>
                        <Slider
                            value={typeof cardOutlineThickness === 'number' ? cardOutlineThickness : 2}
                            onChange={handleOutlineThicknessChanged}
                            min={0}
                            max={4}
                            step={0.1}
                            marks={[
                                { value: 0, label: '0' },
                                { value: 2, label: 'Default' },
                                { value: 4, label: '2×' },
                            ]}
                            style={{ paddingBottom: 20 }}
                        />
                    </FormControl>
                </div>
            </div>
            <Typography variant="subtitle1" style={{ marginBottom: 12 }}>Card Image</Typography>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileSelected}
            />
            <Grid container spacing={1} alignItems="center">
                {renderImagesList()}
            </Grid>
            <CardImageCropDialog
                open={cropOpen}
                imageUrl={cropSrc}
                onCancel={closeCrop}
                onConfirm={handleCropConfirm}
            />
        </div>
    )
}
